import React, { useState, useEffect, useCallback, useRef } from 'react';
import { SortableTableHeader, useSortState, sortData } from '../../../components/SortableTableHeader';
import { LoadingSpinner, EmptyState } from '../../../components/layout/SharedComponents';
import api from '../../../services/api';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { Textarea } from '../../../components/ui/textarea';
import { Badge } from '../../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { useExpenditureLabels, useDecimalFormat } from '../../../contexts/ExpenditureLabelsContext';
import DataLookupField from '../../../components/DataLookupField';
import {
  Plus, Edit, Trash2, Save, Search, Download, X,
  FileText, Users, ChevronRight, ChevronDown, Bell,
  Lock, Eye, Pencil, Settings, Package
} from 'lucide-react';

export function JobTitlesPanel() {
  const [jobTitles, setJobTitles] = useState([]);
  const [jtSort, handleJtSort] = useSortState('code', 'asc');
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingTitle, setEditingTitle] = useState(null);
  const [deletingCode, setDeletingCode] = useState(null);
  const [newTitle, setNewTitle] = useState({ code: '', title: '', description: '' });

  useEffect(() => {
    fetchJobTitles();
  }, []);

  const fetchJobTitles = async () => {
    try {
      const res = await api.get('/api/admin/job-titles', { params: { include_inactive: true } });
      setJobTitles(res.data.job_titles || []);
    } catch (err) {
      if (err?.response?.status >= 500) toast.error('Failed to load job titles');
    }
    setLoading(false);
  };

  const handleCreate = async () => {
    if (!newTitle.code || !newTitle.title) {
      toast.error('Code and Title are required');
      return;
    }
    try {
      await api.post('/api/admin/job-titles', newTitle);
      toast.success('Job title created');
      setNewTitle({ code: '', title: '', description: '' });
      setShowAddModal(false);
      fetchJobTitles();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Create failed');
    }
  };

  const handleUpdate = async () => {
    try {
      await api.put(`/api/admin/job-titles/${editingTitle.code}`, {
        title: editingTitle.title,
        description: editingTitle.description,
        is_active: editingTitle.is_active
      });
      toast.success('Job title updated');
      setEditingTitle(null);
      fetchJobTitles();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    }
  };

  const handleDelete = async (code) => {
    try {
      await api.delete(`/api/admin/job-titles/${code}`);
      toast.success('Job title deleted');
      setDeletingCode(null);
      fetchJobTitles();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
      setDeletingCode(null);
    }
  };

  const confirmDelete = (code) => {
    setDeletingCode(code);
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold">User Maintenance</h1>
          <p className="text-sm text-muted-foreground">Job Title</p>
        </div>
        <Button onClick={() => setShowAddModal(true)}>
          <Plus className="h-4 w-4 mr-1" /> Add New
        </Button>
      </div>

      {jobTitles.length === 0 ? (
        <EmptyState title="No job titles defined" description="Click 'Add New' to create your first job title." />
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <SortableTableHeader label="Code" field="code" sortState={jtSort} onSort={handleJtSort} />
                <SortableTableHeader label="Title" field="title" sortState={jtSort} onSort={handleJtSort} />
                <SortableTableHeader label="Description" field="description" sortState={jtSort} onSort={handleJtSort} />
                <SortableTableHeader label="Status" field="active" sortState={jtSort} onSort={handleJtSort} className="text-center" />
                <th className="w-24"></th>
              </tr>
            </thead>
            <tbody>
              {sortData(jobTitles, jtSort.field, jtSort.dir).map(jt => (
                <tr key={jt.code} className="border-t hover:bg-accent/50">
                  <td className="px-3 py-2 font-mono text-primary">{jt.code}</td>
                  <td className="px-3 py-2 font-medium">{jt.title}</td>
                  <td className="px-3 py-2 text-muted-foreground">{jt.description}</td>
                  <td className="px-3 py-2 text-center">
                    <Badge variant={jt.is_active ? 'default' : 'secondary'}>
                      {jt.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </td>
                  <td className="px-2">
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => setEditingTitle({ ...jt })}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => confirmDelete(jt.code)} className="text-destructive">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add Job Title Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Job Title</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs">Code *</Label>
              <Input
                value={newTitle.code}
                onChange={(e) => setNewTitle(p => ({ ...p, code: e.target.value.toUpperCase() }))}
                placeholder="e.g., MGR, DIR, EXEC"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Title *</Label>
              <Input
                value={newTitle.title}
                onChange={(e) => setNewTitle(p => ({ ...p, title: e.target.value }))}
                placeholder="e.g., Manager, Director"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Description</Label>
              <Input
                value={newTitle.description}
                onChange={(e) => setNewTitle(p => ({ ...p, description: e.target.value }))}
                placeholder="Optional description"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddModal(false)}>Cancel</Button>
            <Button onClick={handleCreate}>
              <Plus className="h-4 w-4 mr-1" /> Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Job Title Modal */}
      {editingTitle && (
        <Dialog open onOpenChange={() => setEditingTitle(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Job Title: {editingTitle.code}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label className="text-xs">Title *</Label>
                <Input
                  value={editingTitle.title}
                  onChange={(e) => setEditingTitle(p => ({ ...p, title: e.target.value }))}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Description</Label>
                <Input
                  value={editingTitle.description || ''}
                  onChange={(e) => setEditingTitle(p => ({ ...p, description: e.target.value }))}
                />
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={editingTitle.is_active}
                  onCheckedChange={(v) => setEditingTitle(p => ({ ...p, is_active: v }))}
                />
                <Label className="text-sm">Active</Label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditingTitle(null)}>Cancel</Button>
              <Button onClick={handleUpdate}>
                <Save className="h-4 w-4 mr-1" /> Save
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Delete Confirmation Modal */}
      {deletingCode && (
        <Dialog open onOpenChange={() => setDeletingCode(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Job Title</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-muted-foreground">
              Are you sure you want to delete the job title <strong>"{deletingCode}"</strong>? 
              This action cannot be undone.
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeletingCode(null)}>Cancel</Button>
              <Button variant="destructive" onClick={() => handleDelete(deletingCode)}>
                <Trash2 className="h-4 w-4 mr-1" /> Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
